
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { format } from 'date-fns';
import { XCircle, Package } from 'lucide-react';

const QualityRejectedList: React.FC = () => {
  const { data: procurements, isLoading } = useQuery({
    queryKey: ['quality-rejected-procurements'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('procurements')
        .select(`
          *,
          farms:farm_id(farmer_name, region),
          procurement_quality(*),
          items:procurement_items(id, product_name, quantity)
        `)
        .eq('rejection_source', 'quality')
        .order('updated_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      return data;
    }
  });

  const getProductNames = (procurement: any) => {
    const items = procurement.items || [];
    if (items.length === 0) {
      return procurement.product_name || 'Unknown Product';
    }
    if (items.length <= 3) {
      return items.map((i: any) => i.product_name).join(', ');
    }
    return `${items.slice(0, 2).map((i: any) => i.product_name).join(', ')} + ${items.length - 2} more`;
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => (
          <Skeleton key={i} className="h-32" />
        ))}
      </div>
    );
  }

  if (!procurements || procurements.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <XCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No quality-rejected procurements</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {procurements.map((procurement) => {
        const quality = (procurement as any).procurement_quality;
        const items = (procurement as any).items || [];
        return (
          <Card key={procurement.id} className="border-destructive/20">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-lg">
                    {getProductNames(procurement)}
                  </CardTitle>
                  {items.length > 1 && (
                    <Badge variant="secondary" className="text-xs">
                      <Package className="h-3 w-3 mr-1" />
                      {items.length} items
                    </Badge>
                  )}
                </div>
                <Badge variant="destructive">Rejected</Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div>
                  <p className="text-sm text-muted-foreground">Quantity</p>
                  <p className="font-medium">{procurement.total_quantity || 0} kg</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Damage %</p>
                  <p className="font-medium">{quality?.damage_percentage || 0}%</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Rejected On</p>
                  <p className="font-medium">
                    {quality?.quality_rejection_date 
                      ? format(new Date(quality.quality_rejection_date), 'MMM dd, yyyy')
                      : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Source</p>
                  <p className="font-medium">
                    {(procurement as any).farms?.farmer_name || procurement.source_type}
                  </p>
                </div>
              </div>
              <div className="bg-destructive/10 p-3 rounded-md">
                <p className="text-sm text-muted-foreground">Rejection Reason</p>
                <p className="font-medium text-destructive">
                  {quality?.quality_rejection_reason || 'No reason provided'}
                </p>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default QualityRejectedList;
